<template>
    <div id="video-detail-page">
        <!-- Modern Header -->
        <div class="page-header">
            <div class="header-left">
                <a-button type="link" class="back-btn" @click="goBack">
                    <span class="material-icons">arrow_back</span>
                </a-button>
                <div class="header-info" v-if="channel">
                    <span class="channel-badge">
                        <span class="material-icons youtube-icon">smart_display</span>
                        {{ channel.name }}
                    </span>
                    <span class="separator">/</span>
                    <span class="video-badge" v-if="video">{{ video.title | truncate(50) }}</span>
                </div>
            </div>
            <div class="header-actions">
                <a-button type="primary" size="small" @click="syncComments" :loading="syncing">
                    <span class="material-icons btn-icon">sync</span>
                    {{ $t("youtubeChannel.sync") }}
                </a-button>
            </div>
        </div>

        <div class="page-content" v-if="video && !loading">
            <!-- Main Layout: Video + Comments side by side -->
            <div class="content-layout">
                <!-- Video Section (Left/Fixed) -->
                <div class="video-section">
                    <div class="video-player">
                        <iframe
                            :src="`https://www.youtube.com/embed/${video.videoId}?rel=0&modestbranding=1`"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                    </div>
                    <div class="video-details">
                        <h1 class="video-title">{{ video.title }}</h1>
                        <div class="video-stats-row">
                            <div class="stat-chip">
                                <span class="material-icons">visibility</span>
                                {{ formatNumber(video.viewCount) }} {{ $t("youtubeChannel.views") }}
                            </div>
                            <div class="stat-chip">
                                <span class="material-icons">thumb_up</span>
                                {{ formatNumber(video.likeCount) }}
                            </div>
                            <div class="stat-chip">
                                <span class="material-icons">chat_bubble</span>
                                {{ formatNumber(video.commentCount) }}
                            </div>
                            <div class="stat-chip">
                                <span class="material-icons">schedule</span>
                                {{ formatDuration(video.duration) }}
                            </div>
                        </div>
                        <div class="video-meta-row">
                            <span class="meta-item">
                                <span class="material-icons">event</span>
                                {{ formatDate(video.publishedAt) }}
                            </span>
                        </div>
                        <div class="video-description" v-if="video.description">
                            <p :class="{ expanded: descriptionExpanded }">{{ video.description }}</p>
                            <a-button
                                type="link"
                                size="small"
                                @click="descriptionExpanded = !descriptionExpanded"
                                v-if="video.description.length > 200"
                            >
                                {{ descriptionExpanded ? $t("youtubeChannel.showLess") : $t("youtubeChannel.showMore") }}
                            </a-button>
                        </div>
                        <div class="video-tags" v-if="video.tags && video.tags.length > 0">
                            <a-tag v-for="tag in video.tags.slice(0, 8)" :key="tag">{{ tag }}</a-tag>
                            <a-tag v-if="video.tags.length > 8">+{{ video.tags.length - 8 }}</a-tag>
                        </div>
                    </div>
                </div>

                <!-- Comments Section (Right/Scrollable) -->
                <div class="comments-section">
                    <div class="comments-header">
                        <h2 class="comments-title">
                            <span class="material-icons">forum</span>
                            {{ $t("youtubeChannel.comments") }}
                            <span class="count">{{ total }}</span>
                        </h2>
                        <div class="comments-filters">
                            <a-select v-model="filters.status" size="small" style="width: 120px">
                                <a-select-option value="">{{ $t("youtubeChannel.all") }}</a-select-option>
                                <a-select-option value="new">{{ $t("youtubeChannel.new") }}</a-select-option>
                                <a-select-option value="replied">{{ $t("youtubeChannel.replied") }}</a-select-option>
                                <a-select-option value="resolved">{{ $t("youtubeChannel.resolved") }}</a-select-option>
                            </a-select>
                            <a-input-search
                                v-model="filters.search"
                                :placeholder="$t('youtubeChannel.searchComments')"
                                size="small"
                                style="width: 160px"
                                @search="fetchComments"
                            />
                        </div>
                    </div>

                    <div class="comments-list" v-if="!commentsLoading">
                        <div class="comment-item" v-for="comment in comments" :key="comment._id">
                            <img :src="comment.authorProfileImageUrl" class="comment-avatar" v-if="comment.authorProfileImageUrl" />
                            <span class="material-icons avatar-fallback" v-else>account_circle</span>
                            <div class="comment-body">
                                <div class="comment-top">
                                    <span class="author">{{ comment.authorDisplayName }}</span>
                                    <span class="date">{{ formatDate(comment.publishedAt) }}</span>
                                    <span class="status-badge" :class="comment.status">{{ getStatusLabel(comment.status) }}</span>
                                    <span class="moderation-badge" :class="comment.moderationStatus" v-if="comment.moderationStatus">
                                        {{ getModerationLabel(comment.moderationStatus) }}
                                    </span>
                                </div>
                                <div class="comment-text" v-html="comment.text"></div>
                                <div class="comment-bottom">
                                    <span class="likes"><span class="material-icons">thumb_up</span> {{ comment.likeCount || 0 }}</span>
                                    <div class="actions">
                                        <a-button size="small" type="link" @click="openReplyModal(comment)">
                                            <span class="material-icons">reply</span> {{ $t("youtubeChannel.reply") }}
                                        </a-button>
                                        <a-dropdown :trigger="['click']">
                                            <a-button size="small" type="link">
                                                <span class="material-icons">tune</span>
                                            </a-button>
                                            <template #overlay>
                                                <a-menu>
                                                    <a-sub-menu key="status" :title="$t('youtubeChannel.status')">
                                                        <a-menu-item key="new" @click="updateCommentStatus(comment, 'new')">
                                                            {{ $t("youtubeChannel.new") }}
                                                        </a-menu-item>
                                                        <a-menu-item key="replied" @click="updateCommentStatus(comment, 'replied')">
                                                            {{ $t("youtubeChannel.replied") }}
                                                        </a-menu-item>
                                                        <a-menu-item key="resolved" @click="updateCommentStatus(comment, 'resolved')">
                                                            {{ $t("youtubeChannel.resolved") }}
                                                        </a-menu-item>
                                                    </a-sub-menu>
                                                    <a-sub-menu key="mod" :title="$t('youtubeChannel.moderation')">
                                                        <a-menu-item key="published" @click="handleModeration(comment, 'published')">
                                                            <a-icon type="check-circle" style="color: #52c41a" /> {{ $t("youtubeChannel.publish") }}
                                                        </a-menu-item>
                                                        <a-menu-item key="heldForReview" @click="handleModeration(comment, 'heldForReview')">
                                                            <a-icon type="clock-circle" style="color: #faad14" /> {{ $t("youtubeChannel.holdForReview") }}
                                                        </a-menu-item>
                                                        <a-menu-item key="rejected" @click="handleModeration(comment, 'rejected')">
                                                            <a-icon type="close-circle" style="color: #ff4d4f" /> {{ $t("youtubeChannel.reject") }}
                                                        </a-menu-item>
                                                    </a-sub-menu>
                                                </a-menu>
                                            </template>
                                        </a-dropdown>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="empty-comments" v-if="comments.length === 0">
                            <span class="material-icons">chat_bubble_outline</span>
                            <p>{{ $t("youtubeChannel.noCommentsFound") }}</p>
                        </div>
                    </div>

                    <div class="comments-loading" v-else>
                        <a-spin />
                    </div>

                    <div class="comments-pagination" v-if="total > pagination.limit">
                        <a-pagination
                            :current="pagination.page"
                            :total="total"
                            :page-size="pagination.limit"
                            size="small"
                            @change="onPageChange"
                            show-size-changer
                            @showSizeChange="onPageSizeChange"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="page-loading" v-else-if="loading">
            <a-spin size="large" />
            <p>{{ $t("youtubeChannel.loadingVideo") }}</p>
        </div>

        <div class="page-error" v-else>
            <span class="material-icons">error_outline</span>
            <p>{{ $t("youtubeChannel.videoNotFound") }}</p>
            <a-button type="primary" @click="goBack">{{ $t("youtubeChannel.goBack") }}</a-button>
        </div>

        <!-- Reply Modal -->
        <a-modal
            v-model="replyModal.visible"
            :title="$t('youtubeChannel.replyToComment')"
            @ok="submitReply"
            :confirm-loading="replyModal.loading"
            width="500px"
        >
            <div class="reply-original" v-if="replyModal.comment">
                <img :src="replyModal.comment.authorProfileImageUrl" v-if="replyModal.comment.authorProfileImageUrl" />
                <div>
                    <strong>{{ replyModal.comment.authorDisplayName }}</strong>
                    <p v-html="replyModal.comment.text"></p>
                </div>
            </div>
            <a-textarea
                v-model="replyModal.text"
                :placeholder="$t('youtubeChannel.writeYourReply')"
                :rows="4"
                :maxLength="10000"
            />
            <div class="char-count">{{ replyModal.text.length }} / 10000</div>
        </a-modal>
    </div>
</template>

<script>
import { YOUTUBE_API } from "../../../../../api/youtube";
import { mapState } from "vuex";
import moment from "moment";

export default {
    name: "VideoDetailPage",
    filters: {
        truncate: function(value, length) {
            if (!value) return "";
            if (value.length <= length) return value;
            return value.substring(0, length) + "...";
        },
    },
    data() {
        return {
            video: null,
            channel: null,
            loading: true,
            syncing: false,
            commentsLoading: false,
            comments: [],
            total: 0,
            descriptionExpanded: false,
            filters: {
                status: "",
                search: "",
            },
            pagination: {
                page: 1,
                limit: 20,
            },
            replyModal: {
                visible: false,
                comment: null,
                text: "",
                loading: false,
            },
        };
    },
    computed: {
        ...mapState({
            channels: function(state) { return state.channel.channels.filter(function(x) { return x.type === "youtube-comments"; }); },
        }),
        videoId: function() {
            return this.$route.params.videoId;
        },
        channelId: function() {
            return this.$route.params.id;
        },
    },
    methods: {
        goBack: function() {
            this.$router.push("/integrations/youtube-comments/" + this.channelId + "/detail");
        },
        getStatusLabel: function(status) {
            var labels = {
                new: this.$t("youtubeChannel.new"),
                replied: this.$t("youtubeChannel.replied"),
                resolved: this.$t("youtubeChannel.resolved"),
                pending: this.$t("youtubeChannel.pending"),
                rejected: this.$t("youtubeChannel.rejected"),
            };
            return labels[status] || status;
        },
        getModerationLabel: function(status) {
            var labels = {
                published: this.$t("youtubeChannel.published"),
                heldForReview: this.$t("youtubeChannel.heldForReview"),
                rejected: this.$t("youtubeChannel.rejected"),
            };
            return labels[status] || status;
        },
        loadVideo: async function() {
            this.loading = true;
            try {
                var response = await YOUTUBE_API.getVideo(this.videoId);
                this.video = response.data;
                this.channel = this.channels.find(function(x) { return x._id === this.channelId; }.bind(this));
                await this.syncComments();
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("youtubeChannel.errorLoadingVideo"));
            } finally {
                this.loading = false;
            }
        },
        syncComments: async function() {
            this.syncing = true;
            try {
                await YOUTUBE_API.fetchComments(this.channelId, this.videoId);
                await this.fetchComments();
            } catch (err) {
                console.error(err);
            } finally {
                this.syncing = false;
            }
        },
        fetchComments: async function() {
            this.commentsLoading = true;
            try {
                var response = await YOUTUBE_API.listComments({
                    channelId: this.channelId,
                    videoId: this.videoId,
                    status: this.filters.status || undefined,
                    search: this.filters.search || undefined,
                    parentId: null,
                    page: this.pagination.page,
                    limit: this.pagination.limit,
                });
                this.comments = response.data.comments;
                this.total = response.data.total;
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorFetchingComments"));
            } finally {
                this.commentsLoading = false;
            }
        },
        updateCommentStatus: async function(comment, status) {
            try {
                await YOUTUBE_API.updateStatus(comment._id, status);
                comment.status = status;
                this.$message.success(this.$t("youtubeChannel.statusUpdated"));
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorUpdatingStatus"));
            }
        },
        handleModeration: async function(comment, action) {
            var moderationStatus = action;
            var banAuthor = false;

            if (action === "rejected-ban") {
                moderationStatus = "rejected";
                banAuthor = true;
            }

            try {
                await YOUTUBE_API.setModerationStatus(comment._id, this.channelId, moderationStatus, banAuthor);
                comment.moderationStatus = moderationStatus;
                this.$message.success(this.$t("youtubeChannel.moderationSuccess"));
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorModeration"));
            }
        },
        openReplyModal: function(comment) {
            this.replyModal.comment = comment;
            this.replyModal.text = "";
            this.replyModal.visible = true;
        },
        submitReply: async function() {
            if (!this.replyModal.text.trim()) {
                this.$message.warning(this.$t("youtubeChannel.pleaseEnterReply"));
                return;
            }
            this.replyModal.loading = true;
            try {
                await YOUTUBE_API.replyComment(this.replyModal.comment._id, {
                    text: this.replyModal.text,
                    channelId: this.channelId,
                });
                this.replyModal.comment.status = "replied";
                this.$message.success(this.$t("youtubeChannel.replySuccess"));
                this.replyModal.visible = false;
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorSendingReply"));
            } finally {
                this.replyModal.loading = false;
            }
        },
        onPageChange: function(page) {
            this.pagination.page = page;
            this.fetchComments();
        },
        onPageSizeChange: function(current, size) {
            this.pagination.limit = size;
            this.pagination.page = 1;
            this.fetchComments();
        },
        formatNumber: function(num) {
            if (!num) return "0";
            var n = parseInt(num);
            if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
            if (n >= 1000) return (n / 1000).toFixed(1) + "K";
            return n.toLocaleString();
        },
        formatDate: function(date) {
            return moment(date).format("DD MMM YYYY, HH:mm");
        },
        formatDuration: function(duration) {
            if (!duration) return "00:00";
            var match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
            if (!match) return duration;
            var hours = (match[1] || "").replace("H", "");
            var minutes = (match[2] || "").replace("M", "");
            var seconds = (match[3] || "").replace("S", "");
            var result = "";
            if (hours) result += hours.padStart(2, "0") + ":";
            result += (minutes || "0").padStart(2, "0") + ":";
            result += (seconds || "0").padStart(2, "0");
            return result;
        },
    },
    watch: {
        "filters.status": function() {
            this.pagination.page = 1;
            this.fetchComments();
        },
        channels: {
            immediate: true,
            handler: function() {
                var self = this;
                this.channel = this.channels.find(function(x) { return x._id === self.channelId; });
            },
        },
    },
    mounted: function() {
        this.loadVideo();
    },
};
</script>

<style lang="scss" scoped>
#video-detail-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--color-bg-default);

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        background: var(--color-bg-subtle);
        border-bottom: 1px solid var(--color-border-default);

        .header-left {
            display: flex;
            align-items: center;
            gap: 8px;

            .back-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                padding: 0;
                border-radius: 8px;

                .material-icons {
                    font-size: 20px;
                }

                &:hover {
                    background: var(--color-bg-default);
                }
            }

            .header-info {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 14px;

                .channel-badge {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: var(--color-text-default);

                    .youtube-icon {
                        font-size: 18px;
                        color: #ff0000;
                    }
                }

                .separator {
                    color: var(--color-text-default);
                    opacity: 0.5;
                }

                .video-badge {
                    font-weight: 600;
                    color: var(--color-text-strong-default);
                }
            }
        }

        .header-actions {
            .btn-icon {
                font-size: 16px;
                margin-right: 4px;
            }
        }
    }

    .page-content {
        flex: 1;
        overflow: hidden;

        .content-layout {
            display: flex;
            height: 100%;
            gap: 0;

            .video-section {
                width: 55%;
                max-width: 720px;
                display: flex;
                flex-direction: column;
                border-right: 1px solid var(--color-border-default);
                overflow-y: auto;

                .video-player {
                    position: relative;
                    padding-bottom: 56.25%;
                    background: #000;
                    flex-shrink: 0;

                    iframe {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                    }
                }

                .video-details {
                    padding: 20px;

                    .video-title {
                        font-size: 18px;
                        font-weight: 700;
                        margin: 0 0 16px;
                        line-height: 1.4;
                        color: var(--color-text-strong-default);
                    }

                    .video-stats-row {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 10px;
                        margin-bottom: 12px;

                        .stat-chip {
                            display: flex;
                            align-items: center;
                            gap: 6px;
                            padding: 6px 12px;
                            background: var(--color-bg-subtle);
                            border-radius: 20px;
                            font-size: 13px;

                            .material-icons {
                                font-size: 16px;
                                color: #ff0000;
                            }
                        }
                    }

                    .video-meta-row {
                        display: flex;
                        gap: 16px;
                        margin-bottom: 16px;
                        font-size: 13px;
                        color: var(--color-text-default);

                        .meta-item {
                            display: flex;
                            align-items: center;
                            gap: 6px;

                            .material-icons {
                                font-size: 16px;
                            }
                        }
                    }

                    .video-description {
                        margin-bottom: 16px;

                        p {
                            margin: 0;
                            font-size: 14px;
                            line-height: 1.6;
                            max-height: 60px;
                            overflow: hidden;
                            transition: max-height 0.3s;

                            &.expanded {
                                max-height: none;
                            }
                        }
                    }

                    .video-tags {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 6px;

                        .ant-tag {
                            margin: 0;
                            border-radius: 12px;
                            font-size: 12px;
                        }
                    }
                }
            }

            .comments-section {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;

                .comments-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid var(--color-border-default);
                    flex-wrap: wrap;
                    gap: 12px;

                    .comments-title {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 16px;
                        font-weight: 600;
                        margin: 0;

                        .material-icons {
                            font-size: 20px;
                            color: #ff0000;
                        }

                        .count {
                            font-size: 13px;
                            font-weight: 400;
                            color: var(--color-text-default);
                            background: var(--color-bg-subtle);
                            padding: 2px 8px;
                            border-radius: 10px;
                        }
                    }

                    .comments-filters {
                        display: flex;
                        gap: 8px;
                    }
                }

                .comments-list {
                    flex: 1;
                    overflow-y: auto;
                    padding: 12px 16px;

                    .comment-item {
                        display: flex;
                        gap: 12px;
                        padding: 14px;
                        border-radius: 10px;
                        margin-bottom: 8px;
                        background: var(--color-bg-subtle);
                        transition: background 0.15s;

                        &:hover {
                            background: var(--color-bg-default);
                            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
                        }

                        .comment-avatar {
                            width: 40px;
                            height: 40px;
                            border-radius: 50%;
                            flex-shrink: 0;
                        }

                        .avatar-fallback {
                            font-size: 40px;
                            color: var(--color-text-default);
                            flex-shrink: 0;
                        }

                        .comment-body {
                            flex: 1;
                            min-width: 0;

                            .comment-top {
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                margin-bottom: 6px;
                                flex-wrap: wrap;

                                .author {
                                    font-weight: 600;
                                    font-size: 13px;
                                }

                                .date {
                                    font-size: 11px;
                                    color: var(--color-text-default);
                                }

                                .status-badge, .moderation-badge {
                                    padding: 2px 8px;
                                    border-radius: 10px;
                                    font-size: 10px;
                                    font-weight: 500;
                                }

                                .status-badge {
                                    &.new { background: #e6f7ff; color: #1890ff; }
                                    &.replied { background: #f6ffed; color: #52c41a; }
                                    &.resolved { background: #f5f5f5; color: #8c8c8c; }
                                    &.pending { background: #fff7e6; color: #fa8c16; }
                                    &.rejected { background: #fff1f0; color: #f5222d; }
                                }

                                .moderation-badge {
                                    &.published { background: #f6ffed; color: #52c41a; }
                                    &.heldForReview { background: #fff7e6; color: #fa8c16; }
                                    &.rejected { background: #fff1f0; color: #f5222d; }
                                }
                            }

                            .comment-text {
                                font-size: 13px;
                                line-height: 1.5;
                                margin-bottom: 8px;
                                word-wrap: break-word;
                            }

                            .comment-bottom {
                                display: flex;
                                justify-content: space-between;
                                align-items: center;

                                .likes {
                                    display: flex;
                                    align-items: center;
                                    gap: 4px;
                                    font-size: 12px;
                                    color: var(--color-text-default);

                                    .material-icons {
                                        font-size: 14px;
                                    }
                                }

                                .actions {
                                    display: flex;
                                    gap: 4px;

                                    button {
                                        display: flex;
                                        align-items: center;
                                        gap: 4px;
                                        font-size: 12px;
                                        padding: 2px 8px;

                                        .material-icons {
                                            font-size: 14px;
                                        }
                                    }
                                }
                            }
                        }
                    }

                    .empty-comments {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 60px 20px;
                        color: var(--color-text-default);

                        .material-icons {
                            font-size: 48px;
                            margin-bottom: 12px;
                            opacity: 0.4;
                        }
                    }
                }

                .comments-loading {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex: 1;
                }

                .comments-pagination {
                    padding: 12px 20px;
                    border-top: 1px solid var(--color-border-default);
                    display: flex;
                    justify-content: center;
                }
            }
        }
    }

    .page-loading, .page-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1;
        gap: 16px;

        .material-icons {
            font-size: 56px;
            color: var(--color-text-default);
            opacity: 0.5;
        }

        p {
            margin: 0;
            font-size: 15px;
            color: var(--color-text-default);
        }
    }

    .reply-original {
        display: flex;
        gap: 12px;
        padding: 12px;
        background: var(--color-bg-subtle);
        border-radius: 8px;
        margin-bottom: 16px;

        img {
            width: 32px;
            height: 32px;
            border-radius: 50%;
        }

        p {
            margin: 4px 0 0;
            font-size: 13px;
            line-height: 1.5;
        }
    }

    .char-count {
        text-align: right;
        font-size: 11px;
        color: var(--color-text-default);
        margin-top: 8px;
    }
}

@media (max-width: 1024px) {
    #video-detail-page .page-content .content-layout {
        flex-direction: column;

        .video-section {
            width: 100%;
            max-width: none;
            border-right: none;
            border-bottom: 1px solid var(--color-border-default);
            max-height: 50vh;
        }

        .comments-section {
            flex: 1;
            min-height: 300px;
        }
    }
}
</style>
